import type {
  PaymentMethod,
  PaymentMethodResult,
  PaymentRequestOptions,
  PaymentRequestShippingAddress,
  Stripe,
  StripeElements,
} from '@stripe/stripe-js';
import type { Currency } from '@whoop/i18n/types/internationalization';
import type { Address } from 'services/generated/order-service/models/Address';
import type { ParsedCartDTO } from '../types/cartTypes';

export const getPaymentRequestOptionsV2 = (
  cart: ParsedCartDTO,
  currency: Currency,
  stripeAccount: string,
): PaymentRequestOptions => {
  const total = cart.billingDetails.find((item) => item.type === 'TOTAL');

  const displayItems = cart.billingDetails
    // Filter out items without value and the total item
    .filter((item) => Boolean(item.value) && item.type !== 'TOTAL')
    .map((item) => {
      // @ts-expect-error Filtered above.
      const formattedValue = item.value.centAmount;
      return {
        label: item.title,
        amount: formattedValue,
      };
    });

  return {
    country: stripeAccount === 'EU' ? 'IE' : 'US', // company's country
    currency, // order's currency
    total: {
      label: total?.title ?? '',
      amount: total?.value?.centAmount ?? 0,
    },
    displayItems,
    requestPayerName: true,
    requestPayerEmail: true,
    requestPayerPhone: false,
    requestShipping: false,
  };
};

export interface OneTouchDigitalWalletOptionsV2 {
  currency: Currency;
  cart: ParsedCartDTO;
  stripeCountry: string;
}

export const getOneTouchDigitalWalletOptionsV2 = ({
  cart,
  currency,
  stripeCountry,
}: OneTouchDigitalWalletOptionsV2): PaymentRequestOptions => {
  const total = cart.billingDetails.find((item) => item.type === 'TOTAL');

  const displayItems = cart.billingDetails
    // Filter out items without value and the total item
    .filter((item) => Boolean(item.value) && item.type !== 'TOTAL')
    .map((item) => {
      // @ts-expect-error Filtered above.
      const formattedValue = item.value.centAmount;
      return {
        label: item.title,
        amount: formattedValue,
      };
    });

  return {
    country: stripeCountry,
    currency,
    total: {
      label: total?.title ?? '',
      amount: total?.value?.centAmount ?? 0,
    },
    displayItems,
  };
};

export const mapStripeAddressToWhoopAddress = (
  stripeAddress: PaymentRequestShippingAddress | undefined,
  phoneNumber: string | undefined,
  payerName: string | undefined,
): Address | undefined => {
  if (!stripeAddress) {
    return undefined;
  }
  const { country, city, postalCode, addressLine, region, recipient } =
    stripeAddress;

  if (!recipient) {
    return undefined;
  }

  const names = separateNames(payerName);

  if (!names) {
    return undefined;
  }

  return {
    first_name: names.firstName,
    last_name: names.lastName,
    phone: phoneNumber,
    country,
    city,
    line1: addressLine?.[0] || 'Whoop Placeholder line1',
    line2: addressLine?.[1] || '',
    postal_code: postalCode,
    state: region,
    province: region,
  };
};

export const separateNames = (recipientName: string | undefined) => {
  if (!recipientName) {
    return;
  }
  const names = recipientName.split(' ');

  let firstName, lastName;
  if (names.length > 1) {
    firstName = names.slice(0, names.length - 1).join(' ');
    lastName = names[names.length - 1];
  } else {
    firstName = names[0] || ' ';
    lastName = ' ';
  }
  return {
    firstName,
    lastName,
  };
};

export const getPaymentElementPaymentMethod = async (
  stripe?: Stripe | null,
  elements?: StripeElements | null,
): Promise<PaymentMethod | undefined> => {
  if (!elements) {
    return;
  }

  const paymentMethod: PaymentMethodResult | undefined =
    await stripe?.createPaymentMethod({
      elements,
    });
  return paymentMethod?.paymentMethod;
};
